import * as Sentry from "@sentry/browser";
import {MotionGlobalConfig} from "framer-motion";
import {Requester, getCookieFromCookieString} from "reactivated/dist/rpc";

import * as auth from "@client/features/auth";
import * as orm from "@client/features/orm";

import * as dates from "@client/dates";
import * as analytics from "@client/shared/analytics";
import * as utils from "@client/utils";

import {
    App,
    CapacitorCookies,
    CapacitorHttp,
    Directory,
    Encoding,
    Filesystem,
    HotPlugin,
    Network,
    Preferences,
    PushNotifications,
    URLOpenListenerEvent,
    getAppInfo,
    getBackgroundNotification,
} from "@client/hardware";
import type {Action, AppDispatch, RootState} from "@client/store";
import {Schema, getServerData, models, server} from "@reactivated";

const createFetchRequest = async (
    headers: Record<string, string>,
    url: string,
    payload: unknown,
    method: "GET" | "POST",
) => {
    const isGet = method === "GET";
    return fetch(url, {
        method,
        body: isGet ? null : JSON.stringify(payload),
        headers: {
            ...headers,
            ...(isGet ? {} : {"Content-Type": "application/json"}),
            "X-CSRFToken":
                getCookieFromCookieString("csrftoken", document.cookie) ?? "",
        },
    });
};

const createCapacitorRequest = async (
    headers: Record<string, string>,
    url: string,
    payload: unknown,
    method: "GET" | "POST",
) => {
    const BASE_URL = utils.getBaseURL();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const csrfToken = (await CapacitorCookies.getCookies()).csrftoken ?? "";

    const isGet = method === "GET";
    const request = CapacitorHttp.request({
        url: `${BASE_URL}${url}`,
        method: isGet ? "get" : "post",
        responseType: "text",
        data: isGet ? undefined : JSON.stringify(payload),
        headers: {
            ...headers,
            Referer: BASE_URL,
            ...(isGet ? {} : {"Content-Type": "application/json"}),
            "X-CSRFToken": csrfToken,
        },
    });

    const response = await request;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return new Response(response.data, {
        status: response.status,
        headers: response.headers,
    });
};

export const requester: Requester = async (url, payload, method) => {
    const {build, platform, version, appVersion} = await getAppInfo();

    const {store} = await import("@client/store");
    store.dispatch({
        type: "INCREMENT_REQUESTS",
    });

    const headers = {
        Accept: "application/json",
        "X-Client-Build": build.toString(),
        "X-Client-Platform": platform,
        "X-Client-Version": version,
        "X-Client-App-Version": appVersion,
    };

    const response = await (window.cordova == null
        ? createFetchRequest(headers, url, payload, method)
        : createCapacitorRequest(headers, url, payload, method));

    store.dispatch({
        type: "DECREMENT_REQUESTS",
    });

    const scope = Sentry.getCurrentScope();
    const requestId = response.headers.get("x-request-id") ?? "";
    scope.setTag("request_id", requestId);
    scope.setTag("api_version", response.headers.get("x-api-version") ?? "");

    if (response.status === 503) {
        const router = await import("@client/router");
        router.replace("maintenance.index", {});
        return {
            type: "exception",
            exception: "maintenance",
        };
    } else if (response.status === 200) {
        return {
            type: "success",
            data: (await response.json()) as unknown,
        };
    } else if (response.status === 400) {
        return {
            type: "invalid",
            errors: (await response.json()) as unknown,
        };
    } else if (response.status === 401) {
        return {
            type: "unauthorized",
        };
    } else if (response.status === 403) {
        return {
            type: "denied",
            reason: (await response.json()) as unknown,
        };
    }
    return {
        type: "exception",
        exception: await response.text(),
    };
};

export const downloadUpdate =
    (
        update: Schema["rpc_fast_init_output"]["update"],
        updateTrigger: "background" | "pause" | "launch",
    ) =>
    async (): Promise<boolean> => {
        if (update.type == "UPDATE_NOT_REQUIRED") {
            return false;
        }

        const {appVersion, version: fromVersion, platform} = await getAppInfo();

        if (update.type == "NATIVE_UPDATE_REQUIRED") {
            await analytics.track("NATIVE_UPDATE_REQUIRED", {
                update_app_version: appVersion,
                update_from_version: fromVersion,
            });
            const router = await import("@client/router");
            router.replace("store_update_required.index", {});
            return true;
        }

        const {index_file, version, folders, static_files, STATIC_URL} = update;

        // Remove half-downloaded version, if any. Fail silently.
        try {
            await Filesystem.rmdir({
                path: `${version}`,
                directory: Directory.Data,
                recursive: true,
            });
        } catch (error) {
            Sentry.captureException(error);
        }

        // If this changes, see hardware.tsx as well.
        const prefix = platform == "ios" ? "NoCloud/ionic_built_snapshots/" : "";

        try {
            const oldVersions = await Filesystem.readdir({
                path: prefix,
                directory: Directory.Library,
            });

            for (const versionOrFile of oldVersions.files) {
                if (
                    versionOrFile.type == "directory" &&
                    versionOrFile.name.startsWith("v") &&
                    versionOrFile.name != fromVersion
                ) {
                    await Filesystem.rmdir({
                        path: `${prefix}${versionOrFile.name}`,
                        directory: Directory.Library,
                        recursive: true,
                    });
                }
            }
        } catch (error) {
            // Directory doesn't exist yet or errors deleting old versions.
            Sentry.captureException(error);
        }

        try {
            for (const folder of folders) {
                const path = `${version}/${folder}`;
                await Filesystem.mkdir({
                    path,
                    directory: Directory.Data,
                    recursive: true,
                });
            }
            // CloudFlare tunnel caches assets and it appears so does Safari
            // So we add a nonce to force busting cache.
            // const nonce = Math.random();

            const staticFiles = static_files.map(async (asset) => {
                // const url = `${STATIC_URL}${asset}?bust=${nonce}`;
                const url = `${STATIC_URL}${asset}`;
                const path = `${version}/static/${asset}`;
                const response = await CapacitorHttp.get({
                    url,
                    responseType: "blob",
                });

                if (response.status !== 200) {
                    throw new Error(`Failed to download ${asset}`);
                }

                return Filesystem.writeFile({
                    directory: Directory.Data,
                    path,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    data: response.data,
                });
            });

            // TODO: should just be another file.
            const indexFile = Filesystem.writeFile({
                path: `${version}/index.html`,
                data: index_file,
                directory: Directory.Data,
                encoding: Encoding.UTF8,
            });

            await Promise.all([...staticFiles, indexFile]);

            await HotPlugin.setVersion({value: version});

            await analytics.track(
                "UPDATED_APP",
                {
                    update_app_version: appVersion,
                    update_from_version: fromVersion,
                    update_to_version: version,
                    update_trigger: updateTrigger,
                },
                {send_immediately: true},
            );

            /*
            await dispatch(
                dialogs.alert({
                    message: "Update Installed",
                    body: "The app was updated and will now restart.",
                }),
            );
            */

            await HotPlugin.reload();
            return true;
        } catch (errors) {
            Sentry.captureException(errors);
        }

        return false;
    };

export const bootstrap = utils.memoizeFirst(
    async (dispatch: AppDispatch, getState: () => RootState) => {
        const {rpc} = await import("@reactivated");
        rpc.requester = requester;

        MotionGlobalConfig.skipAnimations = window.navigator.webdriver;

        const handleAppUrl = async (rawUrl: string) => {
            const url = new URL(rawUrl);
            const router = await import("@client/router");
            const isReady = getState().boot.isReady;

            if (url.host == "tag") {
                return;
            }

            const emailParam = url.searchParams.get("l");
            if (emailParam != null) {
                void server.core.rpc.track_email_click({
                    email_uuid: emailParam,
                });
            }

            const urlWithQuery = `${url.pathname}${url.search}`;

            if (isReady) {
                // We want com.joyapp:// to avoid reloading dependencies.
                // For example, resuming a workout from the live activity
                // widget.  We want https:// to reload because emails might
                // contain links to days they are already on.
                const method = url.protocol == "https:" ? "replace" : "push";

                if (method == "replace") {
                    const search = await import("@client/features/search");
                    void dispatch(search.loadSearch({reset: true}));
                }
                if (router.getLocatorFromURL(urlWithQuery, method).locator == null) {
                    // The transition below silently no-ops on an unmatched
                    // URL — surface it, a dead deep link is invisible on
                    // device otherwise.
                    Sentry.captureMessage(
                        `Deep link matched no route: ${urlWithQuery}`,
                    );
                }
                router.unsafeTransitionByUrl(urlWithQuery, method);
            } else {
                dispatch({
                    type: "SET_REDIRECT_URL",
                    url: urlWithQuery,
                });
            }
        };

        void App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
            void handleAppUrl(event.url);
        });

        const {context} = getServerData();

        // Must be after analytics. to void circular imports.

        // Calling Preferences and Network and Device methods in boot / top-level await on web has ESM / order / chunking
        // errors in the production build. Only because of registerPlugin and the way Capacitor handles this
        // and rollup's inlineDynamicImports to avoid chunking.
        // Tried latest vite 7 and even rolldown and it still does not work.
        const {value: overridenBaseURL} =
            window.cordova == null
                ? {value: null}
                : await Preferences.get({
                      key: "JOY_API_ENDPOINT",
                  });

        if (overridenBaseURL != null) {
            localStorage.setItem(
                "JOY_API_ENDPOINT",
                overridenBaseURL.includes("localhost")
                    ? `http://${overridenBaseURL}`
                    : `https://${overridenBaseURL}`,
            );
        }

        const {platform, version, appVersion, deviceUUID} =
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            await dispatch(setAppVersion());

        // Note that Network.getStatus() and
        if (window.cordova != null && (await Network.getStatus()).connected === false) {
            return false;
        }

        const date = dates.formatISO(new Date(), {representation: "date"});
        const result = await server.journal.day.fast_init({
            client_version: version,
            client_app_version: appVersion,
            date,
            platform,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });

        if (result.type != "success") {
            return false;
        }

        const {credentials} = result.data;
        localStorage.setItem("MIXPANEL_TOKEN", credentials.MIXPANEL_TOKEN);
        localStorage.setItem("SENTRY_DSN", credentials.SENTRY_DSN);

        dispatch({
            type: "FETCH_CREDENTIALS_SUCCESS",
            credentials,
        });

        void analytics.track("APP_LOADED");

        if (result.data.data != null) {
            await dispatch(auth.postLogin(result.data.data.profile, false));
        }

        const subscriptions = await import("@client/features/subscriptions");
        void dispatch(subscriptions.actions.loadAppleSubscriptions());

        const notifications = context.messages.map((message, index) => ({
            id: index,
            level: 0,
            message: message.message,
            level_tag: message.level_tag,
            from_server: false,
        }));
        dispatch({type: "SET_INITIAL_NOTIFICATIONS", notifications});

        void Network.addListener("networkStatusChange", (status) => {
            dispatch({
                type: status.connected === true ? "CONNECTED" : "DISCONNECTED",
            });
        });

        void App.addListener("pause", () => {
            void (async () => {
                const result = await server.journal.day.fast_init({
                    client_version: version,
                    client_app_version: appVersion,
                    date,
                    platform,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                });

                if (result.type == "success") {
                    void dispatch(downloadUpdate(result.data.update, "pause"));
                }
            })();
        });

        if (platform == "ios") {
            void PushNotifications.addListener(
                "pushNotificationReceived",
                (notification) => {
                    const data =
                        notification.data as models["journal.tasks.NotifcationModel"]["notification"];

                    // Called when app is active and notification is received.
                    // Background notification does not trigger this.
                    console.log("PUSH_FOREGROUND: ", notification, data);

                    void server.core.rpc.apple_ping({
                        device_id: deviceUUID,
                        type: "PUSH_NOTIFICATION_RECEIVED",
                    });

                    void analytics.track(
                        "PUSHED_NOTIFICATION_RECEIVED",
                        {
                            notification_type: "STEPS",
                        },
                        {send_immediately: true},
                    );
                },
            );
            void PushNotifications.addListener(
                "pushNotificationActionPerformed",
                (action) => {
                    const data = action.notification
                        .data as models["journal.tasks.NotifcationModel"]["notification"];

                    // Called when app is in background and the notification is clicked.
                    // Background notification does not trigger this.
                    console.log("PUSH_BACKGROUND_CLICKED: ", action, data);

                    void server.core.rpc.apple_ping({
                        device_id: deviceUUID,
                        type: "PUSH_NOTIFICATION_CLICKED",
                    });

                    void analytics.track(
                        "PUSHED_NOTIFICATION_CLICKED",
                        {
                            notification_type: data.type,
                        },
                        {send_immediately: true},
                    );

                    if (data.type == "UPDATED_PROGRAM") {
                        void import("@client/router").then((router) => {
                            const date = dates.formatISO(new Date(), {
                                representation: "date",
                            });
                            router.replace("journal.workout", {key: date});
                        });
                    }
                },
            );
        }

        window.addEventListener("joyBackgroundNotificationWhileActive", () => {
            void getBackgroundNotification().then((notification) => {
                console.log("Push notification from background: ", notification);
            });
        });

        const waitForProfile = async (timeoutMs: number) => {
            const {store} = await import("@client/store");
            return new Promise<ReturnType<typeof orm.getProfile>>((resolve) => {
                let settled = false;
                let unsubscribe = () => {};
                const settle = (profile: ReturnType<typeof orm.getProfile>) => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    unsubscribe();
                    resolve(profile);
                };
                const timer = setTimeout(() => settle(null), timeoutMs);
                unsubscribe = store.subscribe(() => {
                    const profile = orm.getProfile(getState());
                    if (profile != null) {
                        clearTimeout(timer);
                        settle(profile);
                    }
                });
                // Hydration may have completed between the caller's check and
                // the subscription installing — recheck now, or a quiet store
                // waits out the whole timeout and drops the event anyway.
                const current = orm.getProfile(getState());
                if (current != null) {
                    clearTimeout(timer);
                    settle(current);
                }
            });
        };

        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        window.addEventListener("joyHealthKitObserved", async () => {
            const healthkit = await import("@client/features/healthkit");
            const initialProfile = orm.getProfile(getState());

            // The ping fires before any gate: a missing JOY_HEALTHKIT_OBSERVED
            // must mean the event never reached the webview — not that the
            // store was still hydrating or the user was logged out, which this
            // handler used to silently conflate.
            void server.core.rpc.apple_ping({
                device_id: deviceUUID,
                type: "JOY_HEALTHKIT_OBSERVED",
                data: {has_profile: initialProfile != null},
            });

            // Launch-adjacent observer fires beat fast_init to the store —
            // hold the event for the profile instead of dropping it.
            const profile = initialProfile ?? (await waitForProfile(60_000));
            if (profile == null) {
                return;
            }

            void healthkit.checkAndSyncHealthData({
                profile,
                trace: initialProfile != null ? "observed" : "observed-replay",
            });
        });

        if (platform == "ios") {
            // #1810 pull-to-refresh probe, one user only — see probe.tsx.
            void (async () => {
                const profile =
                    orm.getProfile(getState()) ?? (await waitForProfile(60_000));
                if (profile?.user.id !== 1) {
                    return;
                }
                const probe = await import("@client/features/probe");
                probe.install(deviceUUID);
            })();
        }

        dispatch({
            type: "FINISHED_BOOTSTRAPING",
        });

        return {
            ...result.data,
            redirectUrl: getState().boot.redirectUrl,
        };
    },
);

export const wrappedBootstrap =
    () => async (dispatch: AppDispatch, getState: () => RootState) => {
        return bootstrap(dispatch, getState);
    };

const setAppVersion = () => async (dispatch: AppDispatch) => {
    let appId = "com.joyapp.www";
    const {appVersion, version, build, platform, deviceUUID} = await getAppInfo();

    if (window.cordova != null) {
        appId = (await App.getInfo()).id;
    }

    dispatch({
        type: "SET_APP_VERSION",
        deviceUUID,
        appVersion,
        appId,
        build,
        version,
        platform,
    });

    return {
        deviceUUID,
        appVersion,
        appId,
        build,
        version,
        platform,
    };
};

export type Actions =
    | {
          type: "SET_APP_VERSION";
          deviceUUID: string;
          appId: string;
          version: string;
          build: number;
          appVersion: string;
          platform: "web" | "android" | "ios" | "electron";
      }
    | {
          type: "FINISHED_BOOTSTRAPING";
      }
    | {
          type: "SET_REDIRECT_URL";
          url: string | null;
      };

const initialState = {
    isReady: false,
    redirectUrl: null as string | null,
    appVersion: "",
    deviceUUID: null as string | null,
    appId: null as string | null,
    version: "",
    build: 0,
    platform: null as "web" | "android" | "ios" | "electron" | null,
};

export const reducer = (state = initialState, action: Action): typeof initialState => {
    switch (action.type) {
        case "FINISHED_BOOTSTRAPING":
            return {...state, isReady: true};
        case "SET_REDIRECT_URL":
            return {...state, redirectUrl: action.url};
        case "SET_APP_VERSION":
            return {
                ...state,
                deviceUUID: action.deviceUUID,
                appVersion: action.appVersion,
                appId: action.appId,
                build: action.build,
                version: action.version,
                platform: action.platform,
            };
        default:
            return state;
    }
};
